import { useEffect, useRef, useState } from 'react';

import { useFavorites } from '../lib/favorites.js';
import { promptText } from '../lib/prompt-dialog.js';
import {
  eventToChord,
  formatChord,
  shortcuts,
  suspendShortcuts,
  useShortcutVersion,
  type Command,
} from '../lib/shortcuts/index.js';
import { useSettingsView } from './SettingsView.js';
import { IconButton, SelectField } from './ui/controls.js';
import { Icon, type IconName } from './ui/icons.js';

const CONTEXT_LABELS: Record<string, string> = {
  global: 'Everywhere',
  scene: 'Scene',
  model: 'Model',
  'model.rig': 'Rig & Animate',
  'model.mesh': 'Mesh edit',
  pixel: 'Pixel',
  sound: 'Sound',
  play: 'Play',
  code: 'Code',
};

export function contextLabel(context: string): string {
  return CONTEXT_LABELS[context] ?? context;
}

interface Pending {
  id: string;
  chord: string;
  /** Replace this chord of the command (editing), else add. */
  replace?: string;
  conflicts: string[];
}

/**
 * Shortcut Manager (Unity's Edit > Shortcuts, Blender's Keymap preferences):
 * every command with its bindings, rebinding by pressing keys, conflict
 * detection, per-binding and global reset, and keymap profiles.
 */
export function ShortcutManager() {
  useShortcutVersion();
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<string>('all');
  const [recording, setRecording] = useState<{ id: string; replace?: string } | null>(null);
  const [byKey, setByKey] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const importRef = useRef<HTMLInputElement | null>(null);
  const focus = useSettingsView((s) => s.shortcutFocus);
  useEffect(() => {
    if (!focus) return;
    setCategory('all');
    if (focus.chord) {
      setByKey(focus.chord);
      setQuery('');
    } else {
      setByKey(null);
      setQuery(focus.query ?? '');
    }
  }, [focus]);
  const favorites = useFavorites((s) => s.ids);
  const toggleFavorite = useFavorites((s) => s.toggle);

  const commands = shortcuts.list();
  const conflicts = shortcuts.conflicts();
  const conflicted = new Set(conflicts.flatMap((c) => c.ids));
  const profiles = shortcuts.profiles();
  const profile = profiles.find((p) => p.id === shortcuts.activeProfile);

  const categories = [...new Set(commands.map((c) => c.category ?? 'Other'))].sort();

  // Record the next chord pressed (dispatch is suspended meanwhile).
  useEffect(() => {
    if (!recording && byKey !== '') return;
    const resume = suspendShortcuts();
    const onKey = (e: KeyboardEvent) => {
      if (['Control', 'Shift', 'Alt', 'Meta'].includes(e.key)) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      if (e.key === 'Escape' && !e.ctrlKey && !e.shiftKey && !e.altKey) {
        setRecording(null);
        if (byKey === '') setByKey(null);
        return;
      }
      const chord = eventToChord(e);
      if (!chord) return;
      if (byKey === '') {
        setByKey(chord);
        return;
      }
      if (recording) {
        const { conflicts: clash } = shortcuts.clashes(recording.id, chord);
        setRecording(null);
        if (clash.length) setPending({ id: recording.id, chord, ...(recording.replace ? { replace: recording.replace } : {}), conflicts: clash });
        else bind(recording.id, chord, recording.replace);
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      resume();
    };
  }, [recording, byKey]);

  function bind(id: string, chord: string, replace?: string) {
    const keys = [...shortcuts.keysFor(id)].filter((k) => k !== replace && k !== chord);
    shortcuts.setKeys(id, replace ? insertAt(keys, shortcuts.keysFor(id).indexOf(replace), chord) : [...keys, chord]);
  }

  function resolve(mode: 'reassign' | 'both') {
    if (!pending) return;
    if (mode === 'reassign')
      for (const other of pending.conflicts) shortcuts.setKeys(other, shortcuts.keysFor(other).filter((k) => k !== pending.chord));
    bind(pending.id, pending.chord, pending.replace);
    setPending(null);
  }

  const needle = query.trim().toLowerCase();
  const rows = commands
    .filter((c) => {
      if (category === 'conflicts') return conflicted.has(c.id);
      if (category === 'changed') return shortcuts.isOverridden(c.id);
      if (category !== 'all' && (c.category ?? 'Other') !== category) return false;
      if (byKey) return shortcuts.keysFor(c.id).includes(byKey);
      if (!needle) return true;
      return `${c.label} ${c.id} ${c.category ?? ''} ${shortcuts.keysFor(c.id).join(' ')}`.toLowerCase().includes(needle);
    })
    .sort((a, b) => (a.category ?? '').localeCompare(b.category ?? '') || a.label.localeCompare(b.label));

  const exportProfile = () => {
    const blob = new Blob([shortcuts.exportProfile()], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${profile?.name ?? 'keymap'}.keymap.json`.replace(/[^\w.-]+/g, '-');
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };

  return (
    <div className="shortcut-manager">
      <div className="sm-toolbar">
        <label className="sm-profile">
          <span className="muted">Keymap</span>
          <SelectField
            value={shortcuts.activeProfile}
            options={profiles.map((p) => ({ value: p.id, label: p.builtin ? p.name : `${p.name} (custom)` }))}
            onChange={(id) => shortcuts.setProfile(id)}
          />
        </label>
        <IconButton
          icon="plus"
          title="New profile from this one"
          onClick={async () => {
            const name = await promptText('Name the new keymap profile', `${profile?.name ?? 'Keymap'} copy`);
            if (name?.trim()) shortcuts.createProfile(name);
          }}
        />
        {profile && !profile.builtin && (
          <>
            <IconButton
              icon="edit"
              title="Rename profile"
              onClick={async () => {
                const name = await promptText('Rename keymap profile', profile.name);
                if (name?.trim()) shortcuts.renameProfile(profile.id, name);
              }}
            />
            <IconButton icon="trash" title="Delete profile" onClick={() => shortcuts.deleteProfile(profile.id)} />
          </>
        )}
        <span className="sm-spacer" />
        <button type="button" className="ghost small" onClick={exportProfile} title="Save this keymap as a .keymap.json file">
          Export
        </button>
        <button type="button" className="ghost small" onClick={() => importRef.current?.click()} title="Load a .keymap.json as a new profile">
          Import
        </button>
        <input
          ref={importRef}
          type="file"
          accept=".json,application/json"
          hidden
          onChange={async (e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (!file) return;
            const id = shortcuts.importProfile(await file.text());
            setNotice(id ? `Imported "${file.name}".` : `"${file.name}" is not a keymap file.`);
          }}
        />
        <button
          type="button"
          className="ghost small danger"
          disabled={!commands.some((c) => shortcuts.isOverridden(c.id))}
          onClick={() => shortcuts.resetAll()}
          title="Remove every change made to this profile"
        >
          Reset all
        </button>
      </div>
      {profile?.description && <p className="sm-desc muted">{profile.description}</p>}
      {notice && (
        <p className="sm-notice" onClick={() => setNotice(null)}>
          {notice}
        </p>
      )}

      <div className="sm-body">
        <nav className="sm-cats" aria-label="Shortcut categories">
          {[
            ['all', 'All commands'],
            ['changed', 'Changed'],
            ...(conflicts.length ? [['conflicts', `Conflicts (${conflicts.length})`]] : []),
          ].map(([id, label]) => (
            <button key={id} type="button" className={`sm-cat${category === id ? ' is-active' : ''}${id === 'conflicts' ? ' is-warn' : ''}`} onClick={() => setCategory(id!)}>
              {label}
            </button>
          ))}
          <div className="sm-cat-sep" />
          {categories.map((c) => (
            <button key={c} type="button" className={`sm-cat${category === c ? ' is-active' : ''}`} onClick={() => setCategory(c)}>
              {c}
            </button>
          ))}
        </nav>

        <div className="sm-main">
          <div className="sm-search">
            <Icon name="search" size={13} />
            {byKey !== null ? (
              <span className="sm-bykey">
                {byKey === '' ? 'Press a key combination…' : <>Bound to <kbd className="kbd">{formatChord(byKey)}</kbd></>}
              </span>
            ) : (
              <input value={query} placeholder="Filter by name, id or key" onChange={(e) => setQuery(e.target.value)} />
            )}
            <button
              type="button"
              className={`ghost small${byKey !== null ? ' is-on' : ''}`}
              title="Find what a key combination does"
              onClick={() => setByKey(byKey === null ? '' : null)}
            >
              {byKey === null ? 'Search by key' : 'Clear'}
            </button>
          </div>

          {pending && (
            <div className="sm-conflict" role="alert">
              <Icon name="warning" size={14} />
              <span>
                <kbd className="kbd">{formatChord(pending.chord)}</kbd> is already used by{' '}
                <strong>{pending.conflicts.map((id) => shortcuts.get(id)?.label ?? id).join(', ')}</strong> in{' '}
                {contextLabel(shortcuts.get(pending.id)?.context ?? 'global')}.
              </span>
              <button type="button" className="primary small" onClick={() => resolve('reassign')}>
                Reassign
              </button>
              <button type="button" className="ghost small" onClick={() => resolve('both')}>
                Keep both
              </button>
              <button type="button" className="ghost small" onClick={() => setPending(null)}>
                Cancel
              </button>
            </div>
          )}

          <table className="sm-table">
            <thead>
              <tr>
                <th>Command</th>
                <th>Where</th>
                <th>Keys</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <Row
                  key={c.id}
                  command={c}
                  conflicted={conflicted.has(c.id)}
                  favorite={favorites.includes(c.id)}
                  recording={recording?.id === c.id ? (recording.replace ?? '+') : null}
                  onRecord={(replace) => setRecording({ id: c.id, ...(replace ? { replace } : {}) })}
                  onRemove={(chord) => shortcuts.setKeys(c.id, shortcuts.keysFor(c.id).filter((k) => k !== chord))}
                  onFavorite={() => toggleFavorite(c.id)}
                />
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={4} className="muted sm-none">
                    No commands match.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function insertAt(list: string[], index: number, value: string): string[] {
  const out = [...list];
  out.splice(Math.max(0, Math.min(index, out.length)), 0, value);
  return out;
}

function Row({
  command,
  conflicted,
  favorite,
  recording,
  onRecord,
  onRemove,
  onFavorite,
}: {
  command: Command;
  conflicted: boolean;
  favorite: boolean;
  recording: string | null;
  onRecord: (replace?: string) => void;
  onRemove: (chord: string) => void;
  onFavorite: () => void;
}) {
  const keys = shortcuts.keysFor(command.id);
  const locked = Boolean(command.external);
  const changed = shortcuts.isOverridden(command.id);
  return (
    <tr className={`${conflicted ? 'is-conflict' : ''}${changed ? ' is-changed' : ''}`}>
      <td>
        <span className="sm-cmd">
          {command.icon && <Icon name={command.icon as IconName} size={13} />}
          <span>{command.label}</span>
        </span>
        <span className="sm-id">{command.id}</span>
      </td>
      <td className="muted">{contextLabel(command.context)}</td>
      <td>
        <span className="sm-keys">
          {keys.map((k) =>
            recording === k ? (
              <span key={k} className="sm-recording">Press keys…</span>
            ) : (
              <span key={k} className={`sm-chord${conflicted ? ' is-conflict' : ''}`}>
                <button type="button" disabled={locked} title={locked ? 'Bound by the view itself' : 'Click to rebind'} onClick={() => onRecord(k)}>
                  {formatChord(k)}
                </button>
                {!locked && (
                  <button type="button" className="sm-x" aria-label={`Remove ${k}`} onClick={() => onRemove(k)}>
                    <Icon name="x" size={10} />
                  </button>
                )}
              </span>
            ),
          )}
          {recording === '+' && <span className="sm-recording">Press keys… (Esc cancels)</span>}
          {!locked && recording === null && (
            <button type="button" className="sm-add" title="Add a key binding" onClick={() => onRecord()}>
              <Icon name="plus" size={11} />
            </button>
          )}
          {locked && (
            <span className="sm-lock" title="Bound by the view's own key handler; rebinding arrives when it moves onto the registry">
              <Icon name="info" size={12} />
            </span>
          )}
        </span>
      </td>
      <td className="sm-actions">
        {!locked && (
          <>
            <IconButton icon="refresh" title="Reset to profile default" disabled={!changed} onClick={() => shortcuts.resetKeys(command.id)} size={13} />
            <IconButton icon="sparkles" title={favorite ? 'Remove from Quick Favorites' : 'Add to Quick Favorites'} active={favorite} onClick={onFavorite} size={13} />
          </>
        )}
      </td>
    </tr>
  );
}
